import { useEffect, useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { api, resolveImg } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ForkKnife } from "@phosphor-icons/react";

const CATS = [
  { key: "anchor", label: "Lojas e Âncoras" },
  { key: "container", label: "Estandes em Contêineres" },
  { key: "gastronomy", label: "Gastronomia (Kaven)" },
];

export default function Directory() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [stores, setStores] = useState([]);
  const [tab, setTab] = useState("anchor");

  useEffect(() => {
    api.get("/stores").then((res) => setStores(res.data)).catch(() => setStores([]));
    const openKaven = () => setTab("gastronomy");
    window.addEventListener("vm-open-kaven", openKaven);
    return () => window.removeEventListener("vm-open-kaven", openKaven);
  }, []);

  const byCat = (c) => stores.filter((s) => s.category === c);

  return (
    <section id="diretorio" ref={ref} className="relative bg-vm-bg py-24 md:py-32" data-testid="directory-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-vm-gold mb-6">Diretório de Lojas</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="directory-title">
              Tudo em <span className="gold-text italic">Um Só Lugar</span>
            </h2>
          </div>
          <p className="text-vm-mutedFg max-w-sm text-sm leading-relaxed">
            Explore as operações do street mall — de lojas âncoras a estandes em contêineres e a alta gastronomia do Kaven.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-vm-muted border border-vm-border rounded-full p-1.5 h-auto flex-wrap gap-1" data-testid="directory-tabs">
            {CATS.map((c) => (
              <TabsTrigger
                key={c.key}
                value={c.key}
                className="rounded-full px-5 py-2.5 text-sm data-[state=active]:bg-vm-gold data-[state=active]:text-vm-bg text-vm-mutedFg transition-colors"
                data-testid={`directory-tab-${c.key}`}
              >
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATS.map((c) => (
            <TabsContent key={c.key} value={c.key} className="mt-10">
              {c.key === "gastronomy" ? (
                <KavenSpotlight items={byCat("gastronomy")} />
              ) : (
                <StoreGrid items={byCat(c.key)} inView={inView} />
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}

function StoreGrid({ items }) {
  if (!items.length) {
    return <p className="text-vm-mutedFg" data-testid="directory-empty">Nenhuma operação cadastrada nesta categoria ainda.</p>;
  }
  return (
    <AnimatePresence mode="wait">
      <motion.div key={items.length} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="directory-grid">
        {items.map((s, i) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            className="group relative overflow-hidden rounded-2xl border border-vm-border bg-vm-surface hover:border-vm-gold/40 transition-colors duration-500"
            data-testid={`store-card-${s.id}`}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img src={resolveImg(s.image_url)} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-6">
              <h3 className="font-serif text-xl mb-2">{s.name}</h3>
              <p className="text-sm text-vm-mutedFg leading-relaxed line-clamp-3">{s.description}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}

function KavenSpotlight({ items }) {
  const { kaven_description } = useContent();
  const kaven = items[0];
  const img = resolveImg(kaven?.image_url) || "https://images.unsplash.com/photo-1653259038915-7cf0b7a4dd6c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative overflow-hidden rounded-3xl border border-vm-gold/30 grid lg:grid-cols-2" data-testid="kaven-spotlight">
      <div className="relative min-h-[320px] lg:min-h-[460px] overflow-hidden">
        <img src={img} alt="Kaven Restaurante" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-vm-bg/40 lg:to-vm-surface" />
      </div>
      <div className="bg-vm-surface p-10 md:p-14 flex flex-col justify-center">
        <ForkKnife size={40} weight="duotone" className="text-vm-gold mb-6" />
        <p className="text-xs tracking-[0.35em] uppercase text-vm-gold mb-4">Âncora Gastronômica</p>
        <h3 className="font-serif text-4xl md:text-5xl mb-5">{kaven?.name || "Kaven Restaurante"}</h3>
        <p className="text-vm-mutedFg leading-relaxed max-w-md">
          {kaven?.description || kaven_description}
        </p>
        <p className="mt-6 text-sm text-vm-fg/80">Aberto todos os dias · 11h30 às 23h</p>
      </div>
    </motion.div>
  );
}
